import React, { useState } from 'react';
import { Plus, X, Bot, Sparkles } from 'lucide-react';
import { THEME } from '../theme';

const BotCard = ({ bot, darkMode }) => (
  <div className={`group relative p-5 rounded-3xl transition-all duration-300 border overflow-hidden hover:scale-[1.02] active:scale-[0.98] cursor-pointer
    ${darkMode 
      ? 'bg-primary/40 border-white/5 hover:bg-primary/80' 
      : 'bg-white border-slate-100 hover:border-secondary/20'}
  `}>
    <div className="relative z-10 flex justify-between items-start mb-3">
      <div className="flex items-center gap-3">
         <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-medium bg-secondary">
            {bot.name.substring(0, 1).toUpperCase()}
         </div>
         <div>
            <h3 className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-slate-900'}`}>{bot.name}</h3>
            <div className={`flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider mt-0.5 ${bot.status === 'running' ? 'text-emerald-400' : 'text-slate-500'}`}>
              <div className={`w-1.5 h-1.5 rounded-full ${bot.status === 'running' ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'}`} /> 
              {bot.status || 'Idle'}
            </div>
         </div>
      </div>
    </div>
    <div className={`text-[11px] font-light leading-relaxed line-clamp-2 pl-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
        {bot.system_prompt ? `"${bot.system_prompt}"` : <span className="italic opacity-50">No system prompt</span>}
    </div>
  </div>
);

const CreateBotModal = ({ isOpen, onClose, onCreate, loading, darkMode }) => {
    const [name, setName] = useState('');
    const [prompt, setPrompt] = useState('');

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onCreate(name, prompt);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-entry">
            <div className={`w-full max-w-md rounded-3xl p-8 transform transition-all scale-100 ${darkMode ? 'bg-primary border border-white/10' : 'bg-white'}`}>
                <div className="flex justify-between items-center mb-6">
                    <h3 className={`text-xl font-light tracking-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>Create New Agent</h3>
                    <button onClick={onClose} className={`p-2 rounded-full hover:bg-white/10 transition-colors ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}><X size={20} /></button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label className={`block text-xs font-medium uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Agent Name</label>
                        <input 
                            type="text" 
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Sales Closer"
                            className={`w-full p-4 rounded-2xl outline-none border transition-all font-light ${darkMode ? 'bg-primary/50 border-transparent focus:border-secondary text-white placeholder-slate-600' : 'bg-slate-50 border-slate-200 focus:border-secondary text-slate-900 placeholder-slate-400'}`}
                            required
                        />
                    </div>
                    <div>
                        <label className={`block text-xs font-medium uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>System Prompt</label>
                        <textarea 
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="You are a helpful assistant..."
                            className={`w-full p-4 rounded-2xl outline-none border transition-all h-32 resize-none font-light ${darkMode ? 'bg-primary/50 border-transparent focus:border-secondary text-white placeholder-slate-600' : 'bg-slate-50 border-slate-200 focus:border-secondary text-slate-900 placeholder-slate-400'}`}
                        />
                    </div>
                    <button 
                        type="submit" 
                        disabled={loading || !name}
                        className={`w-full py-4 rounded-xl font-medium text-white transform transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2
                          ${loading ? 'opacity-70 cursor-not-allowed' : ''}
                          ${darkMode ? 'bg-secondary hover:bg-secondary/90' : 'bg-secondary hover:bg-secondary/90'}
                        `}
                    >
                        {loading ? 'Creating...' : <><Sparkles size={18} /> Create Agent</>}
                    </button>
                </form>
            </div>
        </div>
    );
};

const BotManager = ({ bots, onCreateBot, loading, darkMode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCreate = async (name, prompt) => {
      const success = await onCreateBot(name, prompt);
      if (success) setIsModalOpen(false);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Add New Bot Card - Placed at top for easy access */}
      <div 
        onClick={() => setIsModalOpen(true)}
        className={`mb-4 p-4 rounded-3xl border-2 border-dashed flex items-center justify-center gap-3 cursor-pointer transition-all group
          ${darkMode 
            ? 'border-white/10 hover:border-secondary/50 hover:bg-secondary/5' 
            : 'border-slate-200 hover:border-secondary/40 hover:bg-secondary/5'}
        `}
      >
        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 ${darkMode ? 'bg-secondary/20 text-secondary' : 'bg-secondary/10 text-secondary'}`}>
          <Plus size={20} />
        </div>
        <span className={`text-sm font-medium ${darkMode ? 'text-slate-400 group-hover:text-secondary' : 'text-slate-500 group-hover:text-secondary'}`}>Create New Agent</span>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3 pb-20">
        {bots.map(bot => (
          <BotCard key={bot.id || bot._id} bot={bot} darkMode={darkMode} />
        ))}
        
        {bots.length === 0 && (
            <div className={`text-center p-8 opacity-50 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <p className="text-xs">No agents found.</p>
            </div>
        )}
      </div>

      <CreateBotModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onCreate={handleCreate} 
        loading={loading} 
        darkMode={darkMode} 
      />
    </div>
  );
};

export default BotManager;
